import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Undo2, Check, Circle, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { cn } from '@/lib/utils';
import { useStacks } from '@/lib/hooks';
import { STACK_ICONS, STACK_COLORS } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

export function RunStackPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { stacks, getStackProgress, completeItem, undoLastCompletion, isLoading: stacksLoading } = useStacks();

    const [showUndo, setShowUndo] = useState(false);
    const [animatingItems, setAnimatingItems] = useState(new Set());
    const [showCelebration, setShowCelebration] = useState(false);
    const [completing, setCompleting] = useState(false);

    const stack = stacks.find(s => s.id === id);
    const progress = getStackProgress(id);

    const icon = stack ? (STACK_ICONS.find(i => i.name === stack.icon) || STACK_ICONS[0]) : null;
    const color = stack ? (STACK_COLORS.find(c => c.name === stack.color) || STACK_COLORS[0]) : null;

    const progressPercent = progress ? (progress.completedToday / progress.totalItems) * 100 : 0;
    const isComplete = progress?.isComplete;

    // Celebrate and redirect when complete
    useEffect(() => {
        if (isComplete && !showCelebration && progress?.completedToday > 0) {
            setShowCelebration(true);

            const duration = 2000;
            const end = Date.now() + duration;
            // Pink/Purple/Blue gradient colors
            const colors = ['#e63a87', '#9b4de0', '#3b82f6', '#06b6d4'];

            const frame = () => {
                confetti({
                    particleCount: 3,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0, y: 0.7 },
                    colors: colors,
                    gravity: 0.8,
                    scalar: 0.9,
                });
                confetti({
                    particleCount: 3,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1, y: 0.7 },
                    colors: colors,
                    gravity: 0.8,
                    scalar: 0.9,
                });

                if (Date.now() < end) {
                    requestAnimationFrame(frame);
                }
            };

            frame();

            setTimeout(() => {
                navigate('/', { replace: true });
            }, 2500);
        }
    }, [isComplete, showCelebration, progress?.completedToday, navigate]);

    useEffect(() => {
        if (showUndo) {
            const timer = setTimeout(() => setShowUndo(false), 5000);
            return () => clearTimeout(timer);
        }
    }, [showUndo]);

    const handleComplete = useCallback(async (itemId) => {
        if (progress?.completedItemIds.has(itemId) || completing) return;

        setAnimatingItems(prev => new Set([...prev, itemId]));
        setCompleting(true);

        try {
            await completeItem(id, itemId);
            setShowUndo(true);

            if (navigator.vibrate) {
                navigator.vibrate(10);
            }
        } catch (err) {
            toast.error('Failed to complete item');
        } finally {
            setCompleting(false);
            setTimeout(() => {
                setAnimatingItems(prev => {
                    const next = new Set(prev);
                    next.delete(itemId);
                    return next;
                });
            }, 300);
        }
    }, [id, completeItem, progress?.completedItemIds, completing]);

    const handleUndo = useCallback(async () => {
        try {
            await undoLastCompletion(id);
            setShowUndo(false);
            setShowCelebration(false);
            toast('Undone', { duration: 2000 });
        } catch (err) {
            toast.error('Failed to undo');
        }
    }, [id, undoLastCompletion]);

    if (stacksLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!stack || !progress) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <p className="text-muted-foreground">Stack not found</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Header */}
            <header className="sticky top-0 bg-background border-b border-border z-10">
                <div className="max-w-lg mx-auto px-4 py-3">
                    <div className="flex items-center gap-3 mb-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            className="shrink-0 -ml-2"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>

                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={cn(
                                "w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0",
                                color.lightClass
                            )}>
                                {icon.emoji}
                            </div>
                            <div className="min-w-0">
                                <h1 className="font-semibold text-foreground truncate">
                                    {stack.name}
                                </h1>
                                <p className="text-xs text-muted-foreground">
                                    {progress.completedToday} of {progress.totalItems} completed
                                </p>
                            </div>
                        </div>
                    </div>

                    <Progress
                        value={progressPercent}
                        className="h-1.5"
                        indicatorClassName={cn(color.class, "transition-all duration-300")}
                    />
                </div>
            </header>

            {/* Items list */}
            <main className="flex-1 max-w-lg mx-auto w-full px-4 py-4">
                <div className="space-y-2">
                    {(stack.items || [])
                        .sort((a, b) => a.position - b.position)
                        .map((item) => {
                            const isCompleted = progress.completedItemIds.has(item.id);
                            const isAnimating = animatingItems.has(item.id);
                            const isCurrentItem = !isCompleted &&
                                (stack.items || []).filter(i => i.position < item.position)
                                    .every(i => progress.completedItemIds.has(i.id));

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleComplete(item.id)}
                                    disabled={isCompleted || showCelebration || completing}
                                    className={cn(
                                        "w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all duration-150",
                                        isCompleted
                                            ? "bg-muted/50"
                                            : isCurrentItem
                                                ? "bg-card border border-primary/30 shadow-soft"
                                                : "bg-card border border-border",
                                        isAnimating && "scale-[1.02] bg-success-light",
                                        !isCompleted && !showCelebration && "hover:border-primary/40 active:scale-[0.99]"
                                    )}
                                >
                                    <div className={cn(
                                        "w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all",
                                        isCompleted
                                            ? "bg-success border-success"
                                            : isCurrentItem
                                                ? "border-primary"
                                                : "border-border"
                                    )}>
                                        {isCompleted && (
                                            <Check className="w-3.5 h-3.5 text-success-foreground" />
                                        )}
                                        {isCurrentItem && !isCompleted && (
                                            <Circle className="w-2 h-2 text-primary fill-primary" />
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <p className={cn(
                                            "font-medium text-sm",
                                            isCompleted && "text-muted-foreground line-through"
                                        )}>
                                            {item.name}
                                        </p>
                                        {item.minimum_version && !isCompleted && (
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {item.minimum_version}
                                            </p>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                </div>

                {/* Completion celebration */}
                {showCelebration && (
                    <div className="mt-8 text-center animate-scale-in">
                        <div className="inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-4 bg-success">
                            <Sparkles className="w-8 h-8 text-success-foreground" />
                        </div>
                        <h2 className="font-bold text-xl text-foreground mb-1">Done!</h2>
                        <p className="text-muted-foreground text-sm">You showed up. That's what counts.</p>
                        <p className="text-xs text-muted-foreground/60 mt-2">Heading back home...</p>
                    </div>
                )}
            </main>

            {/* Undo button */}
            {showUndo && !showCelebration && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 animate-slide-up">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleUndo}
                        className="shadow-elevated gap-2"
                    >
                        <Undo2 className="w-4 h-4" />
                        Undo
                    </Button>
                </div>
            )}
        </div>
    );
}
